import * as React from "react";

import { SidebarInset, SidebarProvider, SidebarTrigger } from "~/components/ui/sidebar";
import { cn, FOCUS_RING } from "~/lib/utils";

export interface AppShellProps {
  sidebar: React.ReactNode;
  topBar?: React.ReactNode;
  pageHeader?: React.ReactNode;
  rightInspector?: React.ReactNode;
  /** Mail is a workspace: no page padding, no extra header chrome. */
  flush?: boolean;
  children: React.ReactNode;
}

/**
 * Application frame. Primary navigation and the Account menu live in `sidebar`
 * (AppSidebar). This chrome must not mint Inbox / Domains / Mailboxes / Settings
 * links or a second account button — e2e locates those by exact accessible name.
 */
export function AppShell({
  sidebar,
  topBar,
  pageHeader,
  rightInspector,
  flush = false,
  children,
}: AppShellProps): React.ReactElement {
  return (
    <SidebarProvider className={flush ? "h-svh" : "min-h-svh"}>
      {sidebar}
      <SidebarInset id="main-content" tabIndex={-1} className={cn("min-w-0", FOCUS_RING)}>
        {/*
          * A floor is not a height. While this said `min-h-svh` unconditionally
          * the workspace grew to fit its content, so the document scrolled
          * instead of the panes, the Compose button and folder rail scrolled
          * away, and `useVirtualizer` windowed over a container that had
          * already rendered every row.
          */}
        <div
          className={cn(
            "flex flex-col",
            flush ? "h-svh overflow-hidden" : "min-h-svh overflow-y-auto",
          )}
        >
          <header
            data-slot="app-shell-mobile-header"
            className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-background px-3 md:hidden"
          >
            <SidebarTrigger className={cn("size-10 min-h-10 min-w-10", FOCUS_RING)} />
            <span className="flex min-w-0 items-center gap-2">
              <span
                className="flex size-7 shrink-0 items-center justify-center rounded-md bg-foreground text-[10px] font-bold text-background"
                aria-hidden="true"
              >
                IF
              </span>
              <span className="truncate text-sm font-semibold tracking-tight">InboxFury</span>
            </span>
          </header>
          {topBar}
          {pageHeader}
          <div
            data-slot="app-shell-body"
            className={cn("flex min-h-0 flex-1 flex-col", flush ? "" : "p-6")}
          >
            {children}
          </div>
        </div>
      </SidebarInset>
      {rightInspector}
    </SidebarProvider>
  );
}
